/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { useMemo, useCallback } from 'preact/compat';
import { ReferenceDocument } from '@/quote-management/components';
import { ReferenceDocumentLinkInput } from '@/quote-management/api/sendQuoteTemplateForReview/sendQuoteTemplateForReview';

export interface UseReferenceDocumentLinksParams {
  templateReferenceDocuments?: ReferenceDocument[];
  localReferenceDocuments: ReferenceDocument[];
}

export interface UseReferenceDocumentLinksReturn {
  /**
   * Checks if a document exists in the template reference documents.
   * Matches by uid if both documents have it, otherwise matches by url.
   */
  isDocumentInTemplateData: (document: ReferenceDocument) => boolean;
  /**
   * Returns documents from localReferenceDocuments that are not in templateReferenceDocuments.
   */
  getNewDocuments: () => ReferenceDocument[];
  /**
   * Transforms all reference documents to API format.
   */
  getReferenceDocumentLinks: () => ReferenceDocumentLinkInput[];
  /**
   * Indicates if there are unsaved changes (new documents, removed documents, or modified documents).
   */
  hasUnsavedChanges: boolean;
}

/**
 * Custom hook to manage reference document links logic.
 * Handles document matching, filtering new documents, transformation to API format,
 * and detection of unsaved changes (new, removed, or modified documents).
 * Note: getReferenceDocumentLinks() returns all local reference documents, not just new ones.
 */
export const useReferenceDocumentLinks = (
  params: UseReferenceDocumentLinksParams
): UseReferenceDocumentLinksReturn => {
  const { templateReferenceDocuments, localReferenceDocuments } = params;

  /**
   * Helper function to find a matching document in template data for a given document.
   * Matches by uid if both documents have it, otherwise matches by url.
   * Returns the matching template document or undefined if no match is found.
   */
  const findMatchingTemplateDocument = useCallback(
    (document: ReferenceDocument): ReferenceDocument | undefined => {
      if (!templateReferenceDocuments) {
        return undefined;
      }
      return templateReferenceDocuments.find((templateDoc) => {
        // If both documents have uids, match by uid
        if (document.uid && templateDoc.uid) {
          return templateDoc.uid === document.uid;
        }
        // Otherwise, match by URL
        return templateDoc.url === document.url;
      });
    },
    [templateReferenceDocuments]
  );

  /**
   * Helper function to check if a document is part of the template data.
   * A document is considered part of template data if:
   * - Its uid exists in templateReferenceDocuments (when uid is available), OR
   * - Its url exists in templateReferenceDocuments (when uid is not available)
   */
  const isDocumentInTemplateData = useCallback(
    (document: ReferenceDocument): boolean => {
      return findMatchingTemplateDocument(document) !== undefined;
    },
    [findMatchingTemplateDocument]
  );

  /**
   * Helper function to check if a document has been modified.
   * Compares all properties (name, identifier, url) between a local document and its matching template document.
   * Returns true if any property differs.
   */
  const isDocumentModified = useCallback(
    (localDoc: ReferenceDocument, templateDoc: ReferenceDocument): boolean => {
      // Compare all properties
      return (
        localDoc.name !== templateDoc.name ||
        localDoc.identifier !== templateDoc.identifier ||
        localDoc.url !== templateDoc.url
      );
    },
    []
  );

  /**
   * Returns documents from localReferenceDocuments that are not in templateReferenceDocuments.
   */
  const getNewDocuments = useCallback((): ReferenceDocument[] => {
    if (!templateReferenceDocuments) {
      // If no template documents exist, all local documents are new
      return localReferenceDocuments;
    }
    // Filter to only include documents not in template data
    return localReferenceDocuments.filter((doc) => {
      return !templateReferenceDocuments.some((templateDoc) => {
        // If both documents have uids, match by uid
        if (doc.uid && templateDoc.uid) {
          return templateDoc.uid === doc.uid;
        }
        // Otherwise, match by URL
        return templateDoc.url === doc.url;
      });
    });
  }, [templateReferenceDocuments, localReferenceDocuments]);

  /**
   * Transforms all reference documents to API format.
   */
  const getReferenceDocumentLinks = useCallback((): ReferenceDocumentLinkInput[] => {
    return localReferenceDocuments.map((doc) => ({
      uid: doc.uid,
      name: doc.name,
      identifier: doc.identifier,
      url: doc.url,
    }));
  }, [localReferenceDocuments]);

  /**
   * Checks if there are unsaved changes.
   * Returns true if there are:
   * - New documents: documents in local state that don't exist in template data
   * - Removed documents: documents in template data that don't exist in local state
   * - Modified documents: documents that exist in both but have different properties (name, identifier, or url)
   */
  const hasUnsavedChanges = useMemo(() => {
    if (!templateReferenceDocuments) {
      // If no template documents exist, any documents in local state are unsaved (new)
      return localReferenceDocuments.length > 0;
    }

    // Check for new documents: documents in local state not found in template data
    const hasNewDocuments = localReferenceDocuments.some((localDoc) => {
      return findMatchingTemplateDocument(localDoc) === undefined;
    });

    if (hasNewDocuments) {
      return true;
    }

    // Check for removed documents: documents in template data not found in local state
    const hasRemovedDocuments = templateReferenceDocuments.some((templateDoc) => {
      return !localReferenceDocuments.some((localDoc) => {
        // If both documents have uids, match by uid
        if (localDoc.uid && templateDoc.uid) {
          return localDoc.uid === templateDoc.uid;
        }
        // Otherwise, match by URL
        return localDoc.url === templateDoc.url;
      });
    });

    if (hasRemovedDocuments) {
      return true;
    }

    // Check for modified documents: documents that match but have different properties
    const hasModifiedDocuments = localReferenceDocuments.some((localDoc) => {
      const matchingTemplateDoc = findMatchingTemplateDocument(localDoc);
      // Defensive code: The falsy branch should be unreachable because we've already checked
      // for new documents above. If a document doesn't have a match, we would have
      // returned true in the hasNewDocuments check. This is kept for safety.
      /* istanbul ignore else */
      if (matchingTemplateDoc) {
        return isDocumentModified(localDoc, matchingTemplateDoc);
      }
      /* istanbul ignore next */
      return false;
    });

    return hasModifiedDocuments;
  }, [templateReferenceDocuments, localReferenceDocuments, findMatchingTemplateDocument, isDocumentModified]);

  return {
    isDocumentInTemplateData,
    getNewDocuments,
    getReferenceDocumentLinks,
    hasUnsavedChanges,
  };
};


